import { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Snackbar, Typography } from '@mui/material';
import { resetToastMessage } from '@stores/App/slice';
import './App.css';
import theme from './theme';
import { AppRoutes } from './routes';
import { RootState } from './stores/store';

const App: FC = () => {
  const dispatch = useDispatch();
  const appData = useSelector((state: RootState) => state?.AppData);

  useEffect(() => {
    const gtagId = process.env.REACT_APP_GTAG_ID;

    if (gtagId) {
      // 1. Inject the external Google script
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;
      script.async = true;
      document.head.appendChild(script);

      // 2. Initialize dataLayer and the gtag function
      const win = window as any;
      win.dataLayer = win.dataLayer || [];

      // Defining it this way ensures 'gtag' is available globally
      win.gtag = function () {
        win.dataLayer.push(arguments);
      };

      win.gtag('js', new Date());
      win.gtag('config', gtagId);
    }

    // Google Tag Manager, only set in prod. Shares window.dataLayer with gtag.
    const gtmId = process.env.REACT_APP_GTM_ID;

    if (gtmId) {
      const win = window as any;
      win.dataLayer = win.dataLayer || [];
      win.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleSnackbarExited = () => {
    dispatch(resetToastMessage());
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppRoutes />
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={Boolean(appData?.toast?.open)}
        autoHideDuration={2000}
        onClose={handleSnackbarExited}
        sx={{ zIndex: 15000 }}
      >
        {appData.toast.heading.length ? (
          <Box
            borderRadius={'0.25rem'}
            padding={'0.8rem'}
            sx={{
              backgroundColor: theme.toast[appData?.toast?.type].background,
              boxShadow: '0px 4px 8px 3px #00000026;',
              minWidth: '320px',
            }}
          >
            <Typography
              fontSize={'0.875rem'}
              lineHeight={'1.25rem'}
              fontWeight={700}
              color={theme.toast[appData?.toast?.type].heading || '#FFF'}
            >
              {appData.toast.heading}
            </Typography>
            <Typography
              fontSize={'0.875rem'}
              lineHeight={'1.25rem'}
              fontWeight={400}
              color={theme.toast[appData?.toast?.type].text || '#FFF'}
            >
              {appData?.toast?.toast_message || ''}
            </Typography>
          </Box>
        ) : (
          <Box
            borderRadius={'0.25rem'}
            padding={'0.8rem'}
            sx={{
              backgroundColor: theme.toast[appData?.toast?.type].background,
              boxShadow: '0px 4px 8px 3px #00000026;',
              minWidth: '320px',
            }}
          >
            <Typography
              fontSize={14}
              fontWeight={700}
              lineHeight={'1.25rem'}
              sx={{
                color: theme.toast[appData?.toast?.type].text || '#FFF',
              }}
            >
              {appData?.toast?.toast_message || ''}
            </Typography>
          </Box>
        )}
      </Snackbar>
    </ThemeProvider>
  );
};

export default App;
